import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, take } from 'rxjs/operators';

/**
 * UtmService - Global UTM parameter capture service
 *
 * This service acts as a redundant, framework-level fallback for UTM parameter capture.
 * It supplements the raw JS capture in index.html to handle edge cases where:
 *   - Browser privacy features strip UTMs before the index.html script runs
 *   - The Angular router processes the URL before the vanilla JS IIFE executes
 *   - Service workers serve cached index.html without re-executing scripts
 *
 * It captures UTM parameters from two sources:
 *   1. window.location.search (immediate, synchronous)
 *   2. Angular Router's first NavigationEnd event (after Angular processes the URL)
 *
 * The data is stored in localStorage under the same key ('utm_params') used by the index.html
 * script and consumed by AuthService.applyUtmParamsTo().
 */
@Injectable({ providedIn: 'root' })
export class UtmService {

    private static readonly STORAGE_KEY = 'utm_params';
    private static readonly UTM_PARAM_NAMES = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'];
    private static readonly EXPIRATION_DAYS = 30;

    constructor(private _router: Router) {}

    /**
     * Capture UTM parameters from the current URL.
     * Should be called once during app bootstrap (AppComponent.ngOnInit).
     *
     * Strategy:
     *  1. Immediately try window.location.search (handles the common case)
     *  2. Also listen for Angular's first NavigationEnd to catch cases where
     *     the router might have the UTMs in its parsed URL
     */
    captureUtmParams(): void {
        // 1. Immediate capture from raw browser URL
        this.captureFromWindowLocation();

        // 2. Also capture after Angular router processes the first navigation
        //    This handles edge cases where Angular's URL parsing might differ
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            take(1)
        ).subscribe((event: NavigationEnd) => {
            // Try to extract UTMs from the router's URL (which might include query params)
            this.captureFromRouterUrl(event.urlAfterRedirects || event.url);
        });
    }

    /**
     * Capture UTM params from window.location.search (raw browser URL)
     */
    private captureFromWindowLocation(): void {
        try {
            const params = this.extractUtmParams(window.location.search);
            if (Object.keys(params).length > 0) {
                this.storeUtmParams(params);
                console.log('[UTM Service] Captured UTM parameters from window.location:', params);
            }
        } catch (e) {
            console.error('[UTM Service] Error capturing UTM from window.location:', e);
        }
    }

    /**
     * Capture UTM params from Angular router's processed URL
     */
    private captureFromRouterUrl(url: string): void {
        try {
            // Only capture if localStorage doesn't already have UTM params
            // (index.html or captureFromWindowLocation already stored them)
            const existing = localStorage.getItem(UtmService.STORAGE_KEY);
            if (existing) {
                // UTM params already captured, no need to overwrite
                return;
            }

            // Extract query string from Angular's router URL
            const queryIndex = url.indexOf('?');
            if (queryIndex === -1) {
                return;
            }
            const queryString = url.substring(queryIndex);
            const params = this.extractUtmParams(queryString);
            if (Object.keys(params).length > 0) {
                this.storeUtmParams(params);
                console.log('[UTM Service] Captured UTM parameters from router URL:', params);
            }
        } catch (e) {
            console.error('[UTM Service] Error capturing UTM from router URL:', e);
        }
    }

    /**
     * Extract UTM parameters from a query string
     */
    private extractUtmParams(queryString: string): { [key: string]: string } {
        const params: { [key: string]: string } = {};
        if (!queryString) {
            return params;
        }

        const urlParams = new URLSearchParams(queryString);
        UtmService.UTM_PARAM_NAMES.forEach(param => {
            const value = urlParams.get(param);
            if (value) {
                params[param] = value;
            }
        });

        return params;
    }

    /**
     * Store UTM parameters in localStorage with expiration
     * Uses the same format as the index.html script for compatibility
     */
    private storeUtmParams(params: { [key: string]: string }): void {
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + UtmService.EXPIRATION_DAYS);

        const utmData = {
            params: params,
            timestamp: new Date().toISOString(),
            expiresAt: expirationDate.toISOString()
        };

        localStorage.setItem(UtmService.STORAGE_KEY, JSON.stringify(utmData));
    }
}
